import {
  fromJS
} from 'immutable';
import _ from 'lodash';
import {
  filterMeasurements
} from 'domain/Options/generators/shapes';
import {
  deserializeMeasurements
} from 'domain/Options/utils/serializeMeasurements';
import {
  SELECTION_TYPE_USER
} from 'domain/Options/selectionTypes';
import {
  PLAN_LOADED,
  PLAN_LOADING
} from 'domain/Options/constants';
import * as planParameters from '../planParameters';
import {
  setKitchenIslandMeasurements,
  setWallPanelHeight,
  setWallEdgingStripHeight,
  setOverhangField,
  setFreestandingHobWidth
} from './input';
import {
  loadPlan,
} from '../utils/convertMeasurement';
import {
  setParameter,
  selectWallLocationId,
  selecKitchenIslandWallLocationId,
  selectMaterialId,
  selectExpressionId,
  selectEdgeId,
  selectThicknessId,
  selectSinkMaterialId,
  selectSinkModelId,
  selectSinkFasteningId,
  selectDrainerGroovesId,
  selectSplashbackId,
  selectWallPanelMaterialId,
  selectWallPanelExpressionId,
  selectKitchenIslandId,
  changeShape,
  resetPlan,
  selectKitchenIslandMaterialId,
  selectKitchenIslandExpressionId,
  selectKitchenIslandCoverPanelId,
  selectKitchenIslandEdgeId,
  selectKitchenIslandThicknessId,
  selectOverhangId,
  selectOverhangSideId,
  selectKitchenIslandOverhangId,
  selectKitchenIslandOverhangSideId,
} from './plan';

const restoreMeasurements = (measurementsParameter, fn) => (state) => {
  if (measurementsParameter && measurementsParameter.value) {
    return deserializeMeasurements(measurementsParameter.value).reduce((reducedState, [field, value]) => fn(reducedState, field, value), state);
  } else {
    return state;
  }
};

function addRangeNameToExpressionID(serializedPlan) {
  // Define the pairs of prefix and expression
  const expressionPairs = [
    { prefix: 'karlby', expression: 'birchveneer' },
    { prefix: 'karlby', expression: 'oakveneer' },
    { prefix: 'pinnarp', expression: 'ashveneer' },
    { prefix: 'pinnarp', expression: 'walnutveneer' },
    { prefix: 'karlby', expression: 'beechveneer' }
  ];

  const prefixes = ["karlby", "barkaboda", "mllekulla", "pinnarp", "rsunda", "skogs"];

  // Function to update expressions based on material type and expression pairs
  function updateExpressions(materialKey, expressionKey) {
    let isMaterialWood = serializedPlan.parameters.some(param => param.name === materialKey && param.value === 'wood');

    if (isMaterialWood) {
      serializedPlan.parameters = serializedPlan.parameters.map(param => {
        if (param.name === expressionKey) {
          const expressionPair = expressionPairs.find(pair => pair.expression === param.value);
          if (expressionPair) {
            // Check if the value starts with any of the predefined prefixes
            const alreadyHasPrefix = prefixes.some(prefix => param.value.startsWith(prefix));
            // Prepend the prefix if it does not already start with one of the predefined prefixes
            if (!alreadyHasPrefix) {
              return { ...param, value: expressionPair.prefix + param.value };
            }
          }
        }
        return param;
      });
    }
  }

  // Update main material expressions
  updateExpressions('material', 'expression');
  // Update kitchen island material expressions
  updateExpressions('kitchenIslandMaterial', 'kitchenIslandExpression');

  return serializedPlan;
}

function planLoaded(stateWithOptions, serializedPlan, hasChanges) {
  // Check if any material is 'wood' before calling the function
  const hasWoodMaterial = serializedPlan.parameters.some(param =>
    (param.name === 'material' || param.name === 'kitchenIslandMaterial') && param.value === 'wood');
  // as of Feb 2024, the includeProductNameInExpression is included in the worktop expression id for wood worktops, as several wood worktops has the same expression ID
  // and needs to differ between them by adding the range ID, to make sure that users saving VPC plans before this change can still load the VPC with wood worktop with
  // their old ID, the addRangeNameToExpressionID adds the range ID to the expression, so the solution is backwards compatible.
  // "addRangeNameToExpressionID" Solution should be removed after around 6 months, as plans older than 6 months, that has not been loaded once during the 6 months, will not be relevant anymore
  if (hasWoodMaterial) {
    serializedPlan = addRangeNameToExpressionID(serializedPlan);
  }

  const parameters = _.fromPairs(serializedPlan.parameters.map((param) => [param.name, param]));
  const makeNumericSetter = (name) => (state) => {
    const p = parameters[name];
    if (p) {
      let varx = setParameter(state, name, parseInt(p.value), SELECTION_TYPE_USER);
      return varx;
    } else {
      // saved plan does not have this parameter
      return state;
    }
  };
  const makeObjectSetter = (name, fn) => (state) => {
    const p = parameters[name];
    if (p) {
      return fn(state, p.value, SELECTION_TYPE_USER);
    } else {
      // saved plan does not have this parameter
      return state;
    }
  };

  const makeBoolSetter = (name) => (state) => {
    const p = parameters[name];
    if (p) {
      return state.setIn(['plan', name], p.value === 'true');
    } else {
      return state;
    }
  };

  loadPlan.loaded = true;

  // TODO: handle case where parameter value is not available
  return [
      (state) => changeShape(state, parameters.shape.value, parameters.shape.selectionType, serializedPlan.measurements, parameters.shapeFlipped.value === 'true'),
      makeObjectSetter(planParameters.wallLocation, selectWallLocationId),
      makeObjectSetter(planParameters.material, selectMaterialId),
      makeObjectSetter(planParameters.expression, selectExpressionId),
      makeObjectSetter(planParameters.edge, selectEdgeId),
      makeObjectSetter(planParameters.thickness, selectThicknessId),
      makeObjectSetter(planParameters.sinkMaterial, selectSinkMaterialId),
      makeObjectSetter(planParameters.sinkModel, selectSinkModelId),
      makeObjectSetter(planParameters.sinkFastening, selectSinkFasteningId),
      makeObjectSetter(planParameters.drainerGrooves, selectDrainerGroovesId),
      makeObjectSetter(planParameters.splashback, selectSplashbackId),
      makeObjectSetter(planParameters.wallPanelMaterial, selectWallPanelMaterialId),
      makeObjectSetter(planParameters.wallPanelExpression, selectWallPanelExpressionId),
      makeObjectSetter(planParameters.kitchenIsland, selectKitchenIslandId),
      makeObjectSetter(planParameters.kitchenIslandWallLocation, selecKitchenIslandWallLocationId),
      makeObjectSetter(planParameters.kitchenIslandMaterial, selectKitchenIslandMaterialId),
      makeObjectSetter(planParameters.kitchenIslandExpression, selectKitchenIslandExpressionId),
      makeObjectSetter(planParameters.kitchenIslandCoverPanel, selectKitchenIslandCoverPanelId),
      makeObjectSetter(planParameters.kitchenIslandEdge, selectKitchenIslandEdgeId),
      makeObjectSetter(planParameters.kitchenIslandThickness, selectKitchenIslandThicknessId),
      makeObjectSetter(planParameters.overhang, selectOverhangId),
      makeObjectSetter(planParameters.overhangSide, selectOverhangSideId),
      makeObjectSetter(planParameters.kitchenIslandOverhang, selectKitchenIslandOverhangId),
      makeObjectSetter(planParameters.kitchenIslandOverhangSide, selectKitchenIslandOverhangSideId),
      makeObjectSetter('wallPanelHeight', setWallPanelHeight),
      makeObjectSetter('wallPanelStripHeight', setWallEdgingStripHeight),

      restoreMeasurements(parameters.kitchenIslandMeasurements, setKitchenIslandMeasurements),
      restoreMeasurements(parameters.overhangMeasurements, setOverhangField),
      restoreMeasurements(parameters.freestandingHobMeasurements, setFreestandingHobWidth),

      // Synchronize input fields
      (state) => state.setIn(['shapesFlipped', parameters.shape.value], parameters.shapeFlipped.value === 'true'),
      (state) => state.setIn(['kitchenIslandFlipped', parameters.kitchenIsland ? parameters.kitchenIsland.value : "no-kitchen-island"], parameters.kitchenIslandFlipped ? parameters.kitchenIslandFlipped.value === 'true' : 'false'),
      (state) => state.setIn(['plan', 'kitchenIsland', 'flipped'], parameters.kitchenIslandFlipped ? parameters.kitchenIslandFlipped.value === 'true' : 'false'),
      (state) => state.setIn(['shapeFields', parameters.shape.value], filterMeasurements(state.getIn(['plan', 'plannedShape', 'value', 'dimensions']), fromJS(serializedPlan.measurements))),
    ]
    .concat(planParameters.numericParameters.map(makeNumericSetter))
    .concat(planParameters.boolParameters.map(makeBoolSetter))
    .reduce((state, fn) => fn(state), resetPlan(stateWithOptions, true))
    .setIn(['plan', 'hasChanges'], hasChanges);
}

function loadingReducer(state, action) {
  switch (action.type) {
    case PLAN_LOADED:
      return planLoaded(state, action.plan, action.hasChanges);
    case PLAN_LOADING:
      return state.set('planLoading', action.loading);
    default:
      return state;
  }
}

export default loadingReducer;
